import * as Haptics from 'expo-haptics';
import { useEffect } from 'react';
import { playBreakdeal } from '@/lib/sounds';
import { StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';

type Props = {
  visible: boolean;
  onDone: () => void;
};

const DOC_W = 160;
const DOC_H = 210;
const HALF_W = DOC_W / 2;

function triggerHaptic() {
  void Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
}

// Jagged tear edge drawn with a clip-path-like View arrangement
function DocLines() {
  return (
    <View style={styles.docLines}>
      {[0.22, 0.37, 0.52, 0.67, 0.80].map((top) => (
        <View key={top} style={[styles.docLine, { top: `${top * 100}%` as unknown as number }]} />
      ))}
    </View>
  );
}

export function AgreementTearAnimation({ visible, onDone }: Props) {
  const overlayOpacity = useSharedValue(0);
  const docOpacity     = useSharedValue(0);

  // Left half
  const leftX  = useSharedValue(0);
  const leftY  = useSharedValue(0);
  const leftR  = useSharedValue(0);

  // Right half
  const rightX = useSharedValue(0);
  const rightY = useSharedValue(0);
  const rightR = useSharedValue(0);

  useEffect(() => {
    if (!visible) return;

    // Reset
    overlayOpacity.value = 0;
    docOpacity.value = 0;
    leftX.value = 0; leftY.value = 0; leftR.value = 0;
    rightX.value = 0; rightY.value = 0; rightR.value = 0;

    const easeIn = Easing.in(Easing.quad);

    // Overlay + doc fade in (0–300ms)
    overlayOpacity.value = withTiming(1, { duration: 200 });
    docOpacity.value     = withTiming(1, { duration: 300 });

    // Phase 2: tear (300–900ms)
    const tearDelay = 300;
    const tearDur   = 600;
    const tearEase  = Easing.in(Easing.back(0.5));

    leftX.value  = withDelay(tearDelay, withTiming(-130, { duration: tearDur, easing: tearEase }));
    leftY.value  = withDelay(tearDelay, withTiming(90,   { duration: tearDur, easing: easeIn }));
    leftR.value  = withDelay(tearDelay, withTiming(-28,  { duration: tearDur, easing: easeIn }));

    rightX.value = withDelay(tearDelay, withTiming(130,  { duration: tearDur, easing: tearEase }));
    rightY.value = withDelay(tearDelay, withTiming(90,   { duration: tearDur, easing: easeIn }));
    rightR.value = withDelay(tearDelay, withTiming(28,   { duration: tearDur, easing: easeIn }));

    const hapticTimer = setTimeout(() => { runOnJS(triggerHaptic)(); runOnJS(playBreakdeal)(); }, tearDelay);

    // Fade out at 2000ms
    const fadeTimer = setTimeout(() => {
      overlayOpacity.value = withTiming(0, { duration: 350 }, (finished) => {
        if (finished) runOnJS(onDone)();
      });
    }, 2000);

    return () => {
      clearTimeout(hapticTimer);
      clearTimeout(fadeTimer);
    };
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  const overlayStyle  = useAnimatedStyle(() => ({ opacity: overlayOpacity.value }));
  const docBaseStyle  = useAnimatedStyle(() => ({ opacity: docOpacity.value }));

  const leftStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: leftX.value },
      { translateY: leftY.value },
      { rotate: `${leftR.value}deg` },
    ],
  }));

  const rightStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: rightX.value },
      { translateY: rightY.value },
      { rotate: `${rightR.value}deg` },
    ],
  }));

  if (!visible) return null;

  return (
    <Animated.View style={[styles.overlay, overlayStyle]} pointerEvents="none">
      <Animated.View style={[styles.docContainer, docBaseStyle]}>
        {/* Left half */}
        <Animated.View style={[styles.halfLeft, leftStyle]}>
          <View style={styles.halfLeftInner}>
            <DocLines />
          </View>
          {/* Jagged right edge of left half */}
          <View style={styles.tearEdgeLeft} />
        </Animated.View>

        {/* Right half */}
        <Animated.View style={[styles.halfRight, rightStyle]}>
          {/* Jagged left edge of right half */}
          <View style={styles.tearEdgeRight} />
          <View style={styles.halfRightInner}>
            <DocLines />
          </View>
        </Animated.View>
      </Animated.View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(13,27,42,0.88)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999,
  },
  docContainer: {
    width: DOC_W,
    height: DOC_H,
    flexDirection: 'row',
  },
  halfLeft: {
    width: HALF_W,
    height: DOC_H,
    flexDirection: 'row',
    transformOrigin: 'right center',
  },
  halfLeftInner: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
  },
  halfRight: {
    width: HALF_W,
    height: DOC_H,
    flexDirection: 'row',
    transformOrigin: 'left center',
  },
  halfRightInner: {
    flex: 1,
    backgroundColor: '#F5F5F5',
    overflow: 'hidden',
  },
  // Thin shadow line simulating tear edge
  tearEdgeLeft: {
    width: 3,
    height: DOC_H,
    backgroundColor: 'rgba(0,0,0,0.25)',
  },
  tearEdgeRight: {
    width: 3,
    height: DOC_H,
    backgroundColor: 'rgba(0,0,0,0.15)',
  },
  docLines: {
    ...StyleSheet.absoluteFillObject,
    paddingHorizontal: 14,
    paddingTop: 20,
  },
  docLine: {
    position: 'absolute',
    left: 14,
    right: 14,
    height: 2,
    backgroundColor: 'rgba(0,0,0,0.12)',
    borderRadius: 1,
  },
});
